// Dependencies
import axios from 'axios'

// Services
import { accessHeaders } from "../../lib/cookie"

// Utils
import {
  ConvertUtility as Convert
} from '../utils/convert-utility'

import {
  ClientUtility as Client
} from '../utils/client-utility'

/**
 * Project address properties
 * @typedef ProjectLocation
 * @type {Object}
 * @property {String} moo       - Village number.
 * @property {String} road      - Road name
 * @property {String} latitude  - Latitude
 * @property {String} longitude - Longitude
 * @typedef {LocationDetail & ProjectLocation} ProjectAddress
 */

/**
 * Project name properties
 * @typedef ProjectName
 * @type {Object}
 * @property {String} tha - Name in Thai
 * @property {String} eng - Name in English
 */

/**
 * Project image properties
 * @typedef ProjectImage
 * @type {Object}
 * @property {String} icon  - Project Icon
 * @property {String} cover - Project Cover
 */

/**
 * Project detail properties
 * @typedef ProjectDetail
 * @type {Object}
 * @property {String} id              - Unique ID
 * @property {String} code            - Project Code
 * @property {String} type            - Project Type (NSL or NSH)
 * @property {ProjectName} name       - Project Names
 * @property {ProjectImage} image     - Project Images
 * @property {ProjectAddress} address - Project Address Details
 * @property {String} createdAt       - Creation time
 * @property {String} updatedAt       - Updation time
 */

/**
 * Project filter properties
 * @typedef ProjectFilter
 * @type {Object}
 * @property {String} text  - Search text
 * @property {String} type  - NSL, NSH, or ALL
 * @property {Number} page  - Fetch page
 * @property {Number} limit - Fetch limit
 */

/**
 * Project item properties
 * @typedef ProjectItem
 * @type {Object}
 * @property {String} id        - Unique ID
 * @property {String} code      - Project Code
 * @property {String} location  - Project Location (Amphur Name)
 * @property {ProjectName} name - Project Name
 * @property {String} createdAt - Creation time
 */

/**
 * Project list properties
 * @typedef ProjectList
 * @type {Object}
 * @property {ProjectItem[]} items  - Project items
 * @property {Number} total         - Total count
 */

/**
 * Project create parameters
 * @typedef ProjectCreate
 * @type {Object}
 * @property {String} code            - Project Code
 * @property {String} type            - Project Type (NSL or NSH)
 * @property {ProjectName} name       - Project Names
 * @property {ProjectImage} image     - Project Images
 * @property {ProjectAddress} address - Project Address Details
 */

/**
 * Project creation result properties
 * @typedef CreateResult
 * @type {Object}
 * @property {String} id        - Unique ID
 * @property {String} createdAt - Creation time
 */

/**
 * Project update parameters
 * @typedef ProjectUpdate
 * @type {Object}
 * @property {String} type            - Project Type (NSL or NSH)
 * @property {ProjectName} name       - Project Names
 * @property {ProjectImage} image     - Project Images
 * @property {ProjectAddress} address - Project Address Details
 */

/**
 * Updation result properties
 * @typedef UpdateResult
 * @type {Object}
 * @property {Boolean} updated - Update state
 */

/**
 * Deletion result properties
 * @typedef DeleteResult
 * @type {Object}
 * @property {Boolean} deleted - Delete state
 */

export class NonScProjectClient {

  // Request Endpoint
  static endpoint = (
    `${process.env.ADMIN_HOST}/ruejai-backend-admin/api/project/nonsc`
  )

  /**
   * Request NON-SC project list
   * @param {ProjectFilter} params - Filter parameters
   * @returns {Promise<ProjectList>} Project list
   */
  static async list(params = {}) {

    // Request URL
    const url = `${this.endpoint}/list`

    // Default Result
    let result = {
      total: 0, items: []
    }

    try {

      // Request Headers
      const headers = accessHeaders()

      // Request Project List
      const { data } = await axios.get(
        url, { headers, params }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-project.list', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Fetch project details
   * @param {Number} projectId - Project ID
   * @returns {Promise<ProjectDetail>} Project detail
   */
  static async get(projectId) {
    
    // Request URL
    const url = `${this.endpoint}/${projectId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Project Detail
      const { data } = await axios.get(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-project.get', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request create project
   * @param {ProjectCreate} payload - Create payload
   * @returns {Promise<CreateResult>} Creation result
   */
  static async create(payload) {

    // Request URL
    const url = this.endpoint
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Create Project
      const { data } = await axios.post(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-project.create', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request update project
   * @param {Number} projectId      - Project ID 
   * @param {UpdateParams} payload  - Update payload
   * @returns {Promise<UpdateResult>} Result
   */
  static async update(projectId, payload) {

    // Request URL
    const url = `${this.endpoint}/${projectId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Update Project
      const { data } = await axios.put(
        url, payload, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {
      
      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-project.update', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

  /**
   * Request delete project
   * @param {Number} projectId - Project ID
   * @returns {Promise<DeleteResult>} Result
   */
  static async remove(projectId) {

    // Request URL
    const url = `${this.endpoint}/${projectId}`
    
    // Default Result
    let result = null

    try {
      
      // Request Headers
      const headers = accessHeaders()

      // Request Delete Project
      const { data } = await axios.delete(
        url, { headers }
      )

      // Validate Result
      if (Client.has(data)) {
        result = Convert.key(data.result)
      }
    }
    catch (error) {

      // Error Message
      const { message } = error

      // Error Response
      const { errorCode } = Client.error(
        'nonsc-project.remove', error
      )

      // Execute Error 
      throw new Error(
        errorCode || message
      )
    }

    return result
  }

}